import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { Calendar, ArrowLeft, Loader2, Edit2, Save, X, Upload, ExternalLink, Plus, Trash2, TrendingUp, Target, Flame, User, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ShareButtons } from '@/components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://primestatlab.com';

// ============== PROP HEADER CARD COMPONENT ==============
const PropHeaderCard = ({ pick }) => {
  if (!pick) return null;
  
  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-surface to-card border-l-4 border-primary rounded-r-lg" data-testid="prop-header-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="text-primary" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-foreground uppercase">{pick.player_name}</h3>
            <p className="text-sm text-muted-foreground">{pick.game || 'Game TBD'}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Prop</p>
            <p className="font-mono font-bold text-foreground">{pick.prop_type}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Line</p>
            <p className="font-mono font-bold text-primary text-lg">{pick.line}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Pick</p>
            <p className="font-mono font-bold text-accent">{pick.pick}</p>
          </div>
          {pick.grade && (
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Grade</p>
              <span className={`inline-block px-2 py-1 rounded font-mono font-bold ${
                pick.grade?.startsWith('A') ? 'bg-green-500/20 text-green-400' :
                pick.grade?.startsWith('B') ? 'bg-primary/20 text-primary' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>{pick.grade}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== KEY EDGE SUMMARY BOX COMPONENT ==============
const KeyEdgeSummaryBox = ({ pick }) => {
  if (!pick) return null;
  
  const signals = [
    { label: 'Structural Cushion', value: pick.structural_cushion, show: !!pick.structural_cushion },
    { label: 'Season Hit Rate', value: pick.season_hit_rate, show: !!pick.season_hit_rate },
    { label: 'L10 Hit Rate', value: pick.last_10_hit_rate, show: !!pick.last_10_hit_rate },
    { label: 'H2H Hit Rate', value: pick.h2h_hit_rate, show: !!pick.h2h_hit_rate },
    { label: 'Heat Level', value: pick.heat_level, show: !!pick.heat_level },
  ].filter(s => s.show);
  
  if (signals.length === 0 && !pick.edge_signals) return null;
  
  return (
    <div className="mb-8 p-5 bg-card border border-border rounded-lg relative overflow-hidden" data-testid="key-edge-summary-box">
      {/* Accent corner */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 rounded-bl-full" />
      
      <div className="flex items-center gap-2 mb-4">
        <Flame className="text-primary" size={20} />
        <h4 className="text-lg font-heading font-bold text-foreground uppercase">Key Edge Signals</h4>
        {pick.heat_level && (
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
            pick.heat_level?.toLowerCase().includes('strong') ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
            pick.heat_level?.toLowerCase().includes('moderate') ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
            'bg-muted/20 text-muted-foreground border border-border'
          }`}>
            {pick.heat_level}
          </span>
        )}
      </div>
      
      {/* Edge signals text */}
      {pick.edge_signals && (
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed border-l-2 border-primary/50 pl-3">
          {pick.edge_signals}
        </p>
      )}
      
      {/* Signal grid */}
      {signals.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {signals.slice(0, 4).map((signal, idx) => (
            <div key={idx} className="p-3 bg-surface rounded border border-border/50">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{signal.label}</p>
              <p className="font-mono text-sm text-foreground font-medium truncate">{signal.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== INTERNAL PLAYER HUB LINK COMPONENT ==============
const PlayerHubLink = ({ playerName }) => {
  if (!playerName) return null;
  
  // Convert player name to slug (e.g., "Jayson Tatum" -> "jayson-tatum")
  const playerSlug = playerName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  
  return (
    <Link 
      to={`/players/${playerSlug}`}
      className="inline-flex items-center gap-2 mt-6 mb-8 px-4 py-2 bg-surface hover:bg-card border border-border hover:border-primary rounded-lg transition-all group"
      data-testid="player-hub-link"
    >
      <Target className="text-primary" size={16} />
      <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
        View full {playerName} analysis & history
      </span>
      <ChevronRight className="text-primary group-hover:translate-x-1 transition-transform" size={16} />
    </Link>
  );
};

// ============== MODEL EDGE RANKING TABLE COMPONENT ==============
const ModelEdgeRankingTable = ({ picks, featuredPlayerNames = [] }) => {
  if (!picks || picks.length === 0) return null;
  
  // Sort by PSL prop score (highest first), then by grade
  const sortedPicks = [...picks].sort((a, b) => {
    const scoreA = a.psl_prop_score || 0;
    const scoreB = b.psl_prop_score || 0;
    return scoreB - scoreA;
  });
  
  // Determine which picks are "featured" (in this article)
  const featuredNamesLower = featuredPlayerNames.map(n => n.toLowerCase());
  
  return (
    <div className="mt-12 p-6 bg-card border border-border rounded-lg" data-testid="model-edge-ranking-table">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="text-primary" size={20} />
        <h3 className="text-lg font-heading font-bold text-foreground uppercase">Today's Model Edge Rankings</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        All plays ranked by PSL Prop Score. Featured plays are highlighted.
      </p>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 text-xs text-muted-foreground uppercase tracking-wider">Rank</th>
              <th className="text-left py-2 px-3 text-xs text-muted-foreground uppercase tracking-wider">Player</th>
              <th className="text-left py-2 px-3 text-xs text-muted-foreground uppercase tracking-wider">Prop</th>
              <th className="text-left py-2 px-3 text-xs text-muted-foreground uppercase tracking-wider">Pick</th>
              <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase tracking-wider">Score</th>
              <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase tracking-wider">Grade</th>
              <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase tracking-wider">Edge Type</th>
            </tr>
          </thead>
          <tbody>
            {sortedPicks.map((pick, idx) => {
              const isFeatured = featuredNamesLower.includes(pick.player_name?.toLowerCase());
              const playerSlug = pick.player_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              
              return (
                <tr 
                  key={pick.id} 
                  className={`border-b border-border/50 transition-colors ${
                    isFeatured 
                      ? 'bg-primary/5 hover:bg-primary/10' 
                      : 'opacity-60 hover:opacity-80'
                  }`}
                >
                  <td className="py-3 px-3">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      idx === 0 ? 'bg-primary text-black' :
                      idx === 1 ? 'bg-primary/60 text-black' :
                      idx === 2 ? 'bg-primary/40 text-foreground' :
                      'bg-muted/30 text-muted-foreground'
                    }`}>
                      {idx + 1}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <Link 
                      to={`/players/${playerSlug}`}
                      className={`font-medium hover:text-primary transition-colors ${isFeatured ? 'text-foreground' : 'text-muted-foreground'}`}
                    >
                      {pick.player_name}
                    </Link>
                  </td>
                  <td className="py-3 px-3 text-muted-foreground">{pick.prop_type}</td>
                  <td className="py-3 px-3">
                    <span className={`font-mono ${isFeatured ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                      {pick.pick}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className={`inline-block px-2 py-1 rounded font-mono text-xs ${
                      (pick.psl_prop_score || 0) >= 80 ? 'bg-green-500/20 text-green-400' :
                      (pick.psl_prop_score || 0) >= 60 ? 'bg-primary/20 text-primary' :
                      'bg-muted/20 text-muted-foreground'
                    }`}>
                      {pick.psl_prop_score || '-'}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className={`font-mono ${
                      pick.grade?.startsWith('A') ? 'text-green-400' :
                      pick.grade?.startsWith('B') ? 'text-primary' :
                      'text-yellow-400'
                    }`}>
                      {pick.grade || '-'}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      pick.structural_edge_class?.toLowerCase().includes('elite') 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : pick.structural_edge_class?.toLowerCase().includes('moderate')
                        ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        : 'bg-muted/20 text-muted-foreground border border-border'
                    }`}>
                      {pick.structural_edge_class?.split(' ').slice(0, 2).join(' ') || 'Standard'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const { isAuthenticated, token } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedFeaturedImage, setEditedFeaturedImage] = useState('');
  const [editedSources, setEditedSources] = useState([]);
  const [newSourceTitle, setNewSourceTitle] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [findingRelated, setFindingRelated] = useState(false);
  const [suggestedSources, setSuggestedSources] = useState([]);
  const [todaysPicks, setTodaysPicks] = useState([]);
  const [articlePicks, setArticlePicks] = useState([]);

  // Generate clean meta description for social sharing
  const getMetaDescription = (content, excerpt) => {
    // Use excerpt if available
    if (excerpt && excerpt.length > 20) {
      let cleanExcerpt = excerpt
        .replace(/META DESCRIPTION/gi, '')
        .replace(/SEO TITLE/gi, '')
        .replace(/[#*]/g, '')
        .trim();
      return cleanExcerpt.substring(0, 160);
    }
    // Otherwise extract from content
    if (!content) return 'Expert sports analysis from PrimeStatLab';
    
    // Clean the content and extract first meaningful paragraph
    let cleaned = content
      .replace(/##?\s*\d*\.?\s*/g, '') // Remove headings
      .replace(/\*\*/g, '') // Remove bold
      .replace(/\*/g, '') // Remove italic
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links but keep text
      .replace(/META DESCRIPTION/gi, '') // Remove meta description label
      .replace(/SEO TITLE/gi, '') // Remove SEO title label
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
    
    // Get first 160 chars for meta description
    return cleaned.substring(0, 160) + (cleaned.length > 160 ? '...' : '');
  };

  // Get the full URL for the article image
  const getImageUrl = (featuredImage) => {
    if (!featuredImage) return `${SITE_URL}/og-image.png`;
    // If it's already a full URL, use it
    if (featuredImage.startsWith('http')) return featuredImage;
    // If it's a relative URL, prepend the site URL
    return `${SITE_URL}${featuredImage}`;
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/articles/${slug}`);
        setArticle(response.data);
        setEditedContent(response.data.content);
        setEditedTitle(response.data.title);
        setEditedFeaturedImage(response.data.featured_image || '');
        setEditedSources(response.data.external_sources || []);
      } catch (error) {
        console.error('Failed to fetch article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  // Update document meta tags when article loads
  useEffect(() => {
    if (!article) return;
    
    const title = article.title || 'Article';
    const description = getMetaDescription(article.content, article.excerpt);
    const image = getImageUrl(article.featured_image);
    const url = `${SITE_URL}/article/${slug}`;
    
    // Update document title
    document.title = `${title} | PrimeStatLab`;
    
    // Helper to set or create meta tag
    const setMetaTag = (property, content, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let tag = document.querySelector(`meta[${attr}="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    
    // Set Open Graph tags
    setMetaTag('og:type', 'article');
    setMetaTag('og:url', url);
    setMetaTag('og:title', title);
    setMetaTag('og:description', description);
    setMetaTag('og:image', image);
    setMetaTag('og:site_name', 'PrimeStatLab');
    
    // Set Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:url', url, true);
    setMetaTag('twitter:title', title, true);
    setMetaTag('twitter:description', description, true);
    setMetaTag('twitter:image', image, true);
    
    // Cleanup on unmount
    return () => {
      document.title = 'PrimeStatLab - Expert Sports Analytics';
    };
  }, [article, slug]);

  // Fetch today's picks for the ranking table and match picks mentioned in article
  useEffect(() => {
    const fetchTodaysPicks = async () => {
      try {
        const response = await axios.get(`${API}/picks`);
        const allPicks = response.data || [];
        
        // Get article date (or today) in YYYY-MM-DD format
        const articleDate = article?.created_at ? new Date(article.created_at) : new Date();
        const articleDateStr = articleDate.toISOString().split('T')[0];
        
        // Also get today's date
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        
        // Filter picks from article date OR today
        const relevantPicks = allPicks.filter(pick => {
          const pickDate = new Date(pick.pick_date);
          const pickDateStr = pickDate.toISOString().split('T')[0];
          return pickDateStr === articleDateStr || pickDateStr === todayStr;
        });
        
        setTodaysPicks(relevantPicks);
        
        // If we have article content/title, try to match player names
        if (article?.content || article?.title) {
          const contentLower = ((article.content || '') + ' ' + (article.title || '')).toLowerCase();
          const matchedPicks = allPicks.filter(pick => 
            contentLower.includes(pick.player_name?.toLowerCase())
          );
          setArticlePicks(matchedPicks);
        }
      } catch (error) {
        console.error('Failed to fetch today\'s picks:', error);
      }
    };
    
    if (article) {
      fetchTodaysPicks();
    }
  }, [article]);

  const startEditing = () => {
    setEditedContent(article.content);
    setEditedTitle(article.title);
    setEditedFeaturedImage(article.featured_image || '');
    setEditedSources(article.external_sources || []);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditedContent(article.content);
    setEditedTitle(article.title);
    setEditedFeaturedImage(article.featured_image || '');
    setEditedSources(article.external_sources || []);
    setEditing(false);
  };

  const addSource = () => {
    if (!newSourceTitle.trim() || !newSourceUrl.trim()) {
      toast.error('Please enter both title and URL');
      return;
    }
    setEditedSources([...editedSources, { title: newSourceTitle.trim(), url: newSourceUrl.trim() }]);
    setNewSourceTitle('');
    setNewSourceUrl('');
  };

  const removeSource = (index) => {
    setEditedSources(editedSources.filter((_, i) => i !== index));
  };

  const findRelatedArticles = async () => {
    setFindingRelated(true);
    setSuggestedSources([]);
    try {
      const response = await axios.post(
        `${API}/articles/find-related`,
        { title: editedTitle, content: editedContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestedSources(response.data.suggested_sources || []);
      toast.success('Found related articles!');
    } catch (error) {
      console.error('Error finding related articles:', error);
      toast.error('Failed to find related articles');
    } finally {
      setFindingRelated(false);
    }
  };

  const addSuggestedSource = (source) => {
    setEditedSources([...editedSources, { title: source.title, url: source.url }]);
    setSuggestedSources(suggestedSources.filter(s => s.url !== source.url));
    toast.success('Source added!');
  };

  const saveArticle = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/articles/${article.id}`,
        { 
          title: editedTitle,
          content: editedContent,
          featured_image: editedFeaturedImage || null,
          excerpt: editedContent.substring(0, 200).replace(/[#*]/g, '').trim() + '...',
          external_sources: editedSources
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setArticle({ ...article, title: editedTitle, content: editedContent, featured_image: editedFeaturedImage, external_sources: editedSources });
      setEditing(false);
      toast.success('Article saved!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Clean up the article content - remove schema markup, HTML tags, and metadata sections
  const cleanContent = (content) => {
    if (!content) return '';
    
    let cleaned = content;
    
    // === PHASE 1: Strip HTML tags that break markdown rendering ===
    // Remove <p> and </p> tags (content stored with HTML wrapping breaks ReactMarkdown)
    cleaned = cleaned.replace(/<\/?p>/gi, '\n\n');
    // Remove <strong> and </strong> tags, replace with markdown bold
    cleaned = cleaned.replace(/<strong>/gi, '**');
    cleaned = cleaned.replace(/<\/strong>/gi, '**');
    // Remove <em> and </em> tags, replace with markdown italic
    cleaned = cleaned.replace(/<em>/gi, '*');
    cleaned = cleaned.replace(/<\/em>/gi, '*');
    // Remove <br> tags
    cleaned = cleaned.replace(/<br\s*\/?>/gi, '\n');
    // Remove any other HTML tags
    cleaned = cleaned.replace(/<[^>]+>/g, '');
    
    // === PHASE 2: Decode HTML entities ===
    cleaned = cleaned
      .replace(/&gt;/g, '>')
      .replace(/&lt;/g, '<')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
    
    // === PHASE 3: Unescape markdown characters ===
    // Fix escaped markdown bold (\*\* -> **)
    cleaned = cleaned.replace(/\\\*\\\*/g, '**');
    cleaned = cleaned.replace(/\\\*/g, '*');
    // Fix escaped markdown headings (\## -> ##)
    cleaned = cleaned.replace(/\\#/g, '#');
    // Fix escaped brackets
    cleaned = cleaned.replace(/\\\[/g, '[');
    cleaned = cleaned.replace(/\\\]/g, ']');
    
    // === PHASE 4: Add line breaks around markdown block elements ===
    // Ensure headings have newlines before them
    cleaned = cleaned.replace(/([^\n])(\n?)(#{1,4}\s)/g, '$1\n\n$3');
    // Ensure horizontal rules have newlines
    cleaned = cleaned.replace(/([^\n])\s*---/g, '$1\n\n---');
    cleaned = cleaned.replace(/---\s*([^\n])/g, '---\n\n$1');
    // Ensure list items starting with "- **" are on their own lines
    cleaned = cleaned.replace(/([^\n-])\s+(- \*\*)/g, '$1\n$2');
    // Ensure standalone bold headers (like **Key Edge Signals**) get their own line
    // Match bold text that appears after a value (not at start of line, not part of list)
    cleaned = cleaned.replace(/([A-Za-z0-9%)\-])\s+(\*\*[A-Z][a-zA-Z ]{3,}\*\*)/g, '$1\n\n$2');
    // Ensure blockquote markers get their own line
    cleaned = cleaned.replace(/([^\n])\s+(>\s*\[)/g, '$1\n\n$2');
    // After a heading, if paragraph text follows on same line, split it
    cleaned = cleaned.replace(/(#{1,4}\s[^\n]{5,80}?)\s{2,}([A-Z])/g, '$1\n\n$2');
    // After percentage/stat values followed by a sentence starting with uppercase
    cleaned = cleaned.replace(/(\d+%)\s+([A-Z][a-z])/g, '$1\n\n$2');
    
    // === PHASE 5: Remove metadata sections ===
    // Remove SCHEMA MARKUP SUGGESTION section and everything after it
    const schemaIndex = cleaned.indexOf('SCHEMA MARKUP SUGGESTION');
    if (schemaIndex !== -1) {
      cleaned = cleaned.substring(0, schemaIndex);
    }
    const schemaIndex2 = cleaned.indexOf('Schema Markup Suggestion');
    if (schemaIndex2 !== -1) {
      cleaned = cleaned.substring(0, schemaIndex2);
    }
    
    // Remove code blocks (```)
    cleaned = cleaned.replace(/```[\s\S]*?```/g, '');
    
    // Remove "Internal Linking Suggestions" section
    const internalLinkIndex = cleaned.indexOf('Internal Linking Suggestions');
    if (internalLinkIndex !== -1) {
      const nextHeadingMatch = cleaned.substring(internalLinkIndex + 30).match(/\n##\s/);
      if (nextHeadingMatch) {
        cleaned = cleaned.substring(0, internalLinkIndex) + cleaned.substring(internalLinkIndex + 30 + nextHeadingMatch.index);
      } else {
        cleaned = cleaned.substring(0, internalLinkIndex);
      }
    }
    
    // Remove SEO TITLE and META DESCRIPTION sections
    cleaned = cleaned.replace(/##?\s*(?:\d+\.)?\s*SEO TITLE[^\n]*\n[^\n]*(?:\n|$)/gi, '');
    cleaned = cleaned.replace(/SEO TITLE[^\n]*\n/gi, '');
    cleaned = cleaned.replace(/##?\s*(?:\d+\.)?\s*META DESCRIPTION[^\n]*\n[^\n]*(?:\n|$)/gi, '');
    cleaned = cleaned.replace(/META DESCRIPTION[^\n]*\n/gi, '');
    cleaned = cleaned.replace(/^SEO TITLE[^\n]*\n?/gim, '');
    cleaned = cleaned.replace(/^META DESCRIPTION[^\n]*\n?/gim, '');
    cleaned = cleaned.replace(/^SEO Title:.*?\n/gm, '');
    cleaned = cleaned.replace(/^Meta Description:.*?\n/gm, '');
    
    // Remove section numbers from headings (## 3. INTRODUCTION -> ## INTRODUCTION)
    cleaned = cleaned.replace(/##\s*\d+\.\s*/g, '## ');
    
    // === PHASE 6: Final cleanup ===
    // Clean up multiple blank lines (3+ newlines -> 2)
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    
    // Fix double-encoded UTF-8 characters
    cleaned = cleaned
      .replace(/â€"/g, '—')
      .replace(/â€"/g, '–')
      .replace(/â€™/g, "'")
      .replace(/â€˜/g, "'")
      .replace(/â€œ/g, '"')
      .replace(/â€/g, '"')
      .replace(/â€¦/g, '…')
      .replace(/Â /g, ' ')
      .replace(/Ã©/g, 'é')
      .replace(/Ã¨/g, 'è')
      .replace(/Ã /g, 'à')
      .replace(/â€/g, '"');
    
    return cleaned.trim();
  };

  // Extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    // Match patterns like:
    // https://www.youtube.com/watch?v=VIDEO_ID
    // https://youtu.be/VIDEO_ID
    // https://www.youtube.com/embed/VIDEO_ID
    // https://youtube.com/watch?v=VIDEO_ID
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Custom link renderer that embeds YouTube videos
  const renderLink = ({ href, children }) => {
    const videoId = getYouTubeVideoId(href);
    
    if (videoId) {
      // Render as embedded YouTube video
      return (
        <div className="my-6">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              src={`https://www.youtube.com/embed/${videoId}`}
              title="YouTube video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {children || 'Watch the full breakdown'}
          </p>
        </div>
      );
    }
    
    // Regular link
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
        {children}
      </a>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen" data-testid="loading-indicator">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="container mx-auto px-6 md:px-12 py-12 text-center" data-testid="article-not-found">
          <h1 className="text-3xl font-heading font-bold mb-4">Article Not Found</h1>
          <Link to="/articles" className="text-primary hover:underline">Return to Articles</Link>
        </div>
      </Layout>
    );
  }

  // Prepare URL for share buttons
  const articleUrl = `${SITE_URL}/article/${slug}`;
  const metaDescription = getMetaDescription(article.content, article.excerpt);
  const metaImage = getImageUrl(article.featured_image);

  // Ensure title is never empty for Helmet
  const pageTitle = (article?.title) 
    ? `${article.title} | NBA Player Props | PrimeStatLab`
    : 'Article | NBA Player Props | PrimeStatLab';

  return (
    <Layout>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={articleUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title || 'PrimeStatLab'} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:site_name" content="PrimeStatLab" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title || 'PrimeStatLab'} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      
      <article className="py-12" data-testid="article-detail">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex items-center justify-between mb-8">
            <Link to="/articles" className="inline-flex items-center gap-2 text-sm font-heading uppercase tracking-widest hover:text-primary transition-colors" data-testid="back-link">
              <ArrowLeft size={16} />
              Back to Articles
            </Link>
            
            {/* Edit button - only show for logged in users */}
            {isAuthenticated && !editing && (
              <Button 
                onClick={startEditing}
                variant="outline"
                className="gap-2"
                data-testid="edit-article-button"
              >
                <Edit2 size={16} />
                Edit Article
              </Button>
            )}
            
            {/* Save/Cancel buttons when editing */}
            {editing && (
              <div className="flex gap-2">
                <Button 
                  onClick={cancelEditing}
                  variant="outline"
                  className="gap-2"
                >
                  <X size={16} />
                  Cancel
                </Button>
                <Button 
                  onClick={saveArticle}
                  disabled={saving}
                  className="gap-2 bg-accent text-white"
                  data-testid="save-article-button"
                >
                  {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                  Save Changes
                </Button>
              </div>
            )}
          </div>

          {/* Show featured image when not editing */}
          {!editing && article.featured_image && (
            <div className="aspect-video w-full mb-8 overflow-hidden border border-border">
              <img src={getImageUrl(article.featured_image)} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Editing Mode */}
          {editing ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Article Title
                </label>
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-surface text-foreground text-xl font-bold"
                />
              </div>

              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Featured Image
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  Upload from your computer or paste an image URL
                </p>
                
                {/* Upload Button */}
                <div className="flex gap-3 mb-3">
                  <label className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded cursor-pointer hover:bg-primary/90 transition-colors">
                    <Upload size={16} />
                    <span>Upload Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        
                        try {
                          const response = await axios.post(
                            `${API}/upload-image`,
                            formData,
                            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                          );
                          setEditedFeaturedImage(`${BACKEND_URL}${response.data.url}`);
                          toast.success('Image uploaded!');
                        } catch (error) {
                          toast.error('Failed to upload image');
                        }
                      }}
                    />
                  </label>
                  <span className="text-muted-foreground self-center">or</span>
                </div>
                
                {/* URL Input */}
                <input
                  type="text"
                  value={editedFeaturedImage}
                  onChange={(e) => setEditedFeaturedImage(e.target.value)}
                  placeholder="Paste image URL here..."
                  className="w-full px-4 py-3 rounded-lg border border-border bg-surface text-foreground"
                />
                {editedFeaturedImage && (
                  <div className="mt-3">
                    <img 
                      src={editedFeaturedImage} 
                      alt="Preview" 
                      className="max-h-40 rounded border border-border"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Article Content (Markdown)
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Tip: Paste a YouTube link and it will automatically embed as a video player!
                </p>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="min-h-[500px] font-mono text-sm bg-surface border-border"
                />
              </div>

              {/* External Sources Section */}
              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  External Sources / Related Articles (For SEO)
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  Add links to authoritative sources. This helps with SEO and builds credibility.
                </p>
                
                {/* AI Find Related Button */}
                <div className="mb-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-bold text-primary">AI-Powered Article Finder</h4>
                      <p className="text-xs text-muted-foreground">Automatically find related articles from ESPN, Bleacher Report, etc.</p>
                    </div>
                    <Button 
                      onClick={findRelatedArticles} 
                      disabled={findingRelated}
                      className="gap-2"
                    >
                      {findingRelated ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Finding...
                        </>
                      ) : (
                        <>
                          <ExternalLink size={16} />
                          Find Related Articles
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* AI Suggestions */}
                  {suggestedSources.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-accent">Click to add:</p>
                      {suggestedSources.map((source, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 bg-surface border border-border rounded cursor-pointer hover:border-primary transition-colors"
                          onClick={() => addSuggestedSource(source)}
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{source.title}</p>
                            <p className="text-xs text-muted-foreground">{source.relevance}</p>
                          </div>
                          <Button size="sm" variant="outline" className="gap-1 ml-2">
                            <Plus size={12} /> Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Manual add source */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newSourceTitle}
                    onChange={(e) => setNewSourceTitle(e.target.value)}
                    placeholder="Source title (e.g., ESPN Analysis)"
                    className="flex-1 px-3 py-2 rounded border border-border bg-surface text-foreground text-sm"
                  />
                  <input
                    type="url"
                    value={newSourceUrl}
                    onChange={(e) => setNewSourceUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-3 py-2 rounded border border-border bg-surface text-foreground text-sm"
                  />
                  <Button onClick={addSource} size="sm" className="gap-1">
                    <Plus size={14} /> Add
                  </Button>
                </div>

                {/* List of sources */}
                {editedSources.length > 0 && (
                  <div className="space-y-2">
                    {editedSources.map((source, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-surface border border-border rounded">
                        <ExternalLink size={14} className="text-primary flex-shrink-0" />
                        <span className="flex-1 text-sm truncate">{source.title}</span>
                        <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary truncate max-w-[200px]">
                          {source.url}
                        </a>
                        <button onClick={() => removeSource(index)} className="text-red-500 hover:text-red-400">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Preview */}
              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-muted-foreground mb-2">
                  Live Preview
                </label>
                <div className="bg-surface border border-border rounded-lg p-6">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      h1: ({children}) => <h1 className="text-3xl font-heading font-bold mt-6 mb-4 text-foreground">{children}</h1>,
                      h2: ({children}) => <h2 className="text-2xl font-heading font-bold mt-6 mb-3 text-foreground border-b border-border pb-2">{children}</h2>,
                      h3: ({children}) => <h3 className="text-xl font-heading font-bold mt-4 mb-2 text-foreground">{children}</h3>,
                      p: ({children}) => <p className="mb-3 leading-relaxed text-muted-foreground">{children}</p>,
                      a: renderLink,
                    }}
                  >
                    {cleanContent(editedContent)}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          ) : (
            /* Normal View Mode */
            <>
              <div className="mb-8">
                <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground mb-4" data-testid="article-meta">
                  <Calendar size={12} />
                  {formatDate(article.created_at)}
                  <span>•</span>
                  <span>By {article.author}</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6" data-testid="article-title">{article.title}</h1>
                {article.excerpt && (
                  <p className="text-xl text-muted-foreground leading-relaxed" data-testid="article-excerpt">
                    {article.excerpt
                      .replace(/META DESCRIPTION/gi, '')
                      .replace(/SEO TITLE/gi, '')
                      .replace(/\s+/g, ' ')
                      .trim()}
                  </p>
                )}
              </div>

              {/* Prop Header Cards & Key Edge Summary Boxes for article picks */}
              {articlePicks.length > 0 && (
                <div className="mb-8 space-y-6" data-testid="article-picks-summary">
                  {articlePicks.map((pick) => (
                    <div key={pick.id}>
                      <PropHeaderCard pick={pick} />
                      <KeyEdgeSummaryBox pick={pick} />
                    </div>
                  ))}
                </div>
              )}

              <div className="prose prose-lg prose-invert max-w-none article-content" data-testid="article-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h1: ({children}) => <h1 className="text-4xl font-heading font-bold mt-10 mb-6 text-foreground">{children}</h1>,
                    h2: ({children}) => <h2 className="text-2xl font-heading font-bold mt-8 mb-4 text-foreground border-b border-border pb-2">{children}</h2>,
                    h3: ({children}) => <h3 className="text-xl font-heading font-bold mt-6 mb-3 text-foreground">{children}</h3>,
                    h4: ({children}) => <h4 className="text-lg font-heading font-bold mt-4 mb-2 text-foreground">{children}</h4>,
                    p: ({children}) => <p className="mb-4 leading-relaxed text-muted-foreground">{children}</p>,
                    ul: ({children}) => <ul className="list-disc list-inside mb-4 space-y-2 text-muted-foreground">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal list-inside mb-4 space-y-2 text-muted-foreground">{children}</ol>,
                    li: ({children}) => <li className="leading-relaxed">{children}</li>,
                    strong: ({children}) => <strong className="font-bold text-foreground">{children}</strong>,
                    em: ({children}) => <em className="italic">{children}</em>,
                    blockquote: ({children}) => <blockquote className="border-l-4 border-primary pl-4 my-4 italic text-muted-foreground">{children}</blockquote>,
                    a: renderLink,
                    hr: () => <hr className="my-8 border-border" />,
                  }}
                >
                  {cleanContent(article.content)}
                </ReactMarkdown>
              </div>

              {/* Player Hub Links for each player mentioned */}
              {articlePicks.length > 0 && (
                <div className="mt-8 space-y-2" data-testid="player-hub-links">
                  {articlePicks.map((pick) => (
                    <PlayerHubLink key={pick.id} playerName={pick.player_name} />
                  ))}
                </div>
              )}

              {/* External Sources / Related Reading Section */}
              {article.external_sources && article.external_sources.length > 0 && (
                <div className="mt-12 p-6 bg-surface border border-border rounded-lg" data-testid="external-sources">
                  <h3 className="text-lg font-heading font-bold mb-4 flex items-center gap-2">
                    <ExternalLink size={18} className="text-primary" />
                    Related Reading & Sources
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    For more in-depth analysis and up-to-date information, check out these resources:
                  </p>
                  <ul className="space-y-3">
                    {article.external_sources.map((source, index) => (
                      <li key={index}>
                        <a 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-primary hover:text-accent transition-colors group"
                        >
                          <ExternalLink size={14} className="flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                          <span className="font-medium">{source.title}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Model Edge Ranking Table - Shows all today's plays */}
              {todaysPicks.length > 0 && (
                <ModelEdgeRankingTable 
                  picks={todaysPicks} 
                  featuredPlayerNames={articlePicks.map(p => p.player_name)} 
                />
              )}

              {/* Standard PSL CTA Footer - Auto-appended for SEO */}
              <div className="mt-10 p-6 bg-card border-l-4 border-primary rounded-r-lg">
                <p className="text-muted-foreground mb-3">
                  <strong className="text-foreground">Prime Stat Lab</strong> publishes daily NBA player prop analysis using distribution modeling, matchup data, and opportunity metrics.
                </p>
                <p className="text-muted-foreground text-sm">
                  For daily prop breakdowns and model-driven insights, follow Prime Stat Lab on social media and check back for new NBA prop analysis each day.
                </p>
              </div>
            </>
          )}

          {/* Share Buttons */}
          {!editing && (
            <div className="mt-8 pt-8 border-t border-border">
              <ShareButtons 
                url={articleUrl}
                title={article.title}
                description={metaDescription}
                image={metaImage}
              />
            </div>
          )}

          {/* Responsible Gaming Notice - always at bottom */}
          <div className="mt-12 p-6 bg-surface border border-border rounded-lg">
            <p className="text-sm text-muted-foreground italic text-center">
              This analysis is for informational and entertainment purposes only. Always research independently and gamble responsibly.
            </p>
          </div>
        </div>
      </article>
    </Layout>
  );
}
